import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { Loading } from '../components/Loading'
import { DistortionInfo } from '../components/DistortionInfo'
import type { Session, JournalEntry } from '../types'

export default function SessionDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { get, post } = useApi()

  const [session, setSession] = useState<Session | null>(null)
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Journal modal
  const [showJournalModal, setShowJournalModal] = useState(false)
  const [journalText, setJournalText] = useState('')
  const [savingJournal, setSavingJournal] = useState(false)

  useEffect(() => {
    const loadSession = async () => {
      try {
        const data = await get<{
          session: Session
          journal_entries: JournalEntry[]
        }>(`/api/sessions/${id}`)

        setSession(data.session)
        setJournalEntries(data.journal_entries || [])
      } catch (err) {
        console.error('Failed to load session:', err)
        setError('Session not found')
      } finally {
        setLoading(false)
      }
    }

    loadSession()
  }, [id, get])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const saveJournalEntry = async () => {
    if (!journalText.trim()) return

    setSavingJournal(true)
    try {
      const result = await post<{ entry: JournalEntry }>('/api/journal', {
        text: journalText,
        linked_session_id: id,
      })

      setJournalEntries(prev => [result.entry, ...prev])
      setJournalText('')
      setShowJournalModal(false)
    } catch (err) {
      console.error('Failed to save journal entry:', err)
      alert('Failed to save journal entry')
    } finally {
      setSavingJournal(false)
    }
  }

  if (loading) {
    return (
      <div className="page">
        <Loading message="Loading session..." />
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="page">
        <div className="page-content">
          <div className="empty-state">
            <div className="empty-state-icon">❓</div>
            <h3 className="empty-state-title">{error || 'Session not found'}</h3>
            <Link to="/history" className="btn btn-primary">
              Back to History
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const delta = session.emotion_intensity_post !== null
    ? session.emotion_intensity_post - session.emotion_intensity_pre
    : null

  return (
    <div className="page">
      <div className="page-header">
        <Link to="/history" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>
          ← Back
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Session</h1>
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
            {session.mode === 'voice' ? '🎤' : '⌨️'} {session.mode}
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
          {formatDate(session.created_at)}
        </p>
      </div>

      <div className="page-content" style={{ paddingBottom: '100px' }}>
        {/* Emotion summary */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                Feeling
              </p>
              <p style={{ fontSize: '1.25rem', fontWeight: '600', textTransform: 'capitalize' }}>
                {session.emotion_label}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                {session.emotion_intensity_pre} → {session.emotion_intensity_post ?? '—'}
              </p>
              {delta !== null && (
                <p style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: delta < 0 ? 'var(--color-success)' : delta > 0 ? 'var(--color-error)' : 'var(--color-text-muted)'
                }}>
                  {delta < 0 ? delta : delta > 0 ? `+${delta}` : '0'}
                </p>
              )}
            </div>
          </div>

          {/* Intensity bars */}
          <div style={{ marginTop: '1rem' }}>
            <div className="intensity-item">
              <span className="intensity-label">Before</span>
              <div className="intensity-bar" style={{ flex: 1 }}>
                <div
                  className="intensity-fill intensity-pre"
                  style={{ width: `${session.emotion_intensity_pre * 10}%` }}
                />
              </div>
              <span className="intensity-value">{session.emotion_intensity_pre}</span>
            </div>
            {session.emotion_intensity_post !== null && (
              <div className="intensity-item" style={{ marginTop: '0.25rem' }}>
                <span className="intensity-label">After</span>
                <div className="intensity-bar" style={{ flex: 1 }}>
                  <div
                    className="intensity-fill intensity-post"
                    style={{ width: `${session.emotion_intensity_post * 10}%` }}
                  />
                </div>
                <span className="intensity-value">{session.emotion_intensity_post}</span>
              </div>
            )}
          </div>
        </div>

        {/* Trigger */}
        <div className="detail-section">
          <p className="detail-label">Trigger</p>
          <p className="detail-value">{session.trigger_text}</p>
        </div>

        {/* Automatic thought */}
        <div className="detail-section">
          <p className="detail-label">Automatic Thought</p>
          <p className="detail-value">{session.auto_thought}</p>
        </div>

        {/* Evidence */}
        {(session.evidence_for.length > 0 || session.evidence_against.length > 0) && (
          <div className="detail-section">
            <p className="detail-label">Evidence</p>

            {session.evidence_for.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-error)', marginBottom: '0.25rem' }}>
                  Supporting the thought:
                </p>
                <ul className="evidence-list evidence-for">
                  {session.evidence_for.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {session.evidence_against.length > 0 && (
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginBottom: '0.25rem' }}>
                  Against the thought:
                </p>
                <ul className="evidence-list evidence-against">
                  {session.evidence_against.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Balanced thought */}
        {session.balanced_thought && (
          <div className="detail-section">
            <p className="detail-label">Balanced Thought</p>
            <div className="balanced-thought">
              {session.balanced_thought}
            </div>
          </div>
        )}

        {/* Action step */}
        {session.action_step && (
          <div className="detail-section">
            <p className="detail-label">Action Step</p>
            <div className="action-step">
              {session.action_step}
            </div>
          </div>
        )}

        {/* Distortions with explanations */}
        {session.distortions.length > 0 && (
          <div className="detail-section">
            <DistortionInfo distortions={session.distortions} />
          </div>
        )}

        {/* Time orientation */}
        {session.time_orientation && (
          <div className="detail-section">
            <p className="detail-label">Time Orientation</p>
            <p className="detail-value" style={{ textTransform: 'capitalize' }}>
              {session.time_orientation}
            </p>
          </div>
        )}

        {/* Journal entries */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <p className="detail-label" style={{ margin: 0 }}>Journal Notes</p>
            <button
              onClick={() => setShowJournalModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              + Add Note
            </button>
          </div>

          {journalEntries.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
              No notes yet. Add a reflection or follow-up.
            </p>
          ) : (
            journalEntries.map(entry => (
              <div key={entry.id} className="card" style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>{entry.text}</p>
                <p style={{ fontSize: '0.625rem', color: 'var(--color-text-muted)' }}>
                  {formatDate(entry.created_at)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Journal modal */}
      {showJournalModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'flex-end',
          zIndex: 100
        }}>
          <div style={{
            background: 'var(--color-bg-light)',
            width: '100%',
            padding: '1.5rem',
            borderRadius: '1rem 1rem 0 0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Add Note</h3>
              <button
                onClick={() => setShowJournalModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <textarea
              className="form-textarea"
              value={journalText}
              onChange={(e) => setJournalText(e.target.value)}
              placeholder="How did the action step go? Any follow-up thoughts?"
              rows={4}
              autoFocus
            />

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '1rem' }}
              onClick={saveJournalEntry}
              disabled={!journalText.trim() || savingJournal}
            >
              {savingJournal ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
